<template>
  <table
    :id="`table-` + slugify(label)"
    class="AiMap-section"
    :aria-label="$t(`hotel.map.${label}`)">
    <caption class="AiMap-section--header">
      <ai-icon class="AiMap-section--icon" aria-hidden="true" :name="label" />
      <ai-typo
        class="AiMap-airportsSection--title"
        as="span"
        variant="paragraph-04-bold">
        {{ $t(`hotel.map.${label}`) }}
      </ai-typo>
    </caption>
    <tbody>
      <tr
        v-for="(section, index) of sectionsLoaded"
        :key="`AiMap-section--${index}`"
        class="AiMap-section--item">
        <td>
          <ai-typo as="span" variant="paragraph-04-regular">
            {{ section?.name?.toLowerCase() }}
          </ai-typo>
        </td>
        <td class="AiMap-section--item-distance">
          <ai-typo as="span" variant="paragraph-04-regular">
            {{
              $t(`hotel.map.distanceUnity`, {
                distance: section?.distance?.km,
              })
            }}
          </ai-typo>
        </td>
      </tr>
    </tbody>
    <tfoot v-if="!attach">
      <tr>
        <td>
          <ai-button
            :class="['AiMap-button', { 'AiMap-button--expanded': expanded }]"
            slim
            :icon-right="expanded ? 'chevron' : 'chevron-down'"
            role="button"
            variant="tertiary"
            :label="buttonLabel"
            :aria-expanded="expanded"
            :aria-controls="`table-` + slugify(label)"
            @click="toggleExpanded" />
        </td>
      </tr>
    </tfoot>
  </table>
</template>

<script setup lang="ts">
import type { IconName } from '../../atoms/AiIcon/types';

import type { SurroundingsFragment } from '~~/domains/graphql';
import { slugify } from '~~/helpers';

export interface AiMapSectionProps {
  label: IconName;
  sections:
    | SurroundingsFragment['nearbyAirports']
    | SurroundingsFragment['nearbyAttractions'];
}

const props = withDefaults(defineProps<AiMapSectionProps>(), {
  sections: () => [],
});

const { t } = useI18n();
const itemLength = ref<number>(2);
const expanded = ref<boolean>(false);

const items = computed(() =>
  props.sections?.map(section => {
    const kmDistance = section?.distance?.km;
    const milesDistance = section?.distance?.miles;

    let km = String(kmDistance);
    if (typeof kmDistance !== 'number' || kmDistance <= 1) {
      km = '< 1';
    }

    let miles = String(milesDistance);
    if (typeof milesDistance !== 'number' || milesDistance <= 1) {
      miles = '< 1';
    }

    return {
      ...section,
      distance: {
        km,
        miles,
      },
    };
  }),
);

const sectionsLoaded = computed(() => items.value?.slice(0, itemLength.value));

const buttonLabel = computed<string>(() =>
  expanded.value ? t('hotel.map.buttonLabelLess') : t('hotel.map.buttonLabel'),
);
const attach = computed(() => (items.value?.length ?? 0) <= 2);
const toggleExpanded = () => {
  if (!expanded.value) {
    itemLength.value = items.value?.length ?? 0;
    expanded.value = !expanded.value;
  } else {
    itemLength.value = 2;
    expanded.value = !expanded.value;
  }
};
</script>
<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
.AiMap-section {
  display: flex;
  flex-direction: column;
  &--header {
    margin-bottom: constants.$margin-01;
    align-items: flex-end;
    display: flex;
  }
  &--item {
    margin-bottom: constants.$inner-02;
    justify-content: space-between;
    display: flex;
    text-transform: capitalize;
  }
  &--item-distance {
    min-width: fit-content;
    margin-top: auto;
    margin-left: constants.$margin-01;
  }
  &--icon {
    margin-right: constants.$inner-03;
  }
}
.AiMap-button {
  margin-top: constants.$margin-01;
  text-align: start;
}
</style>
